import { useEffect, createContext } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Modal,
  Space,
  Spin,
  Card,
  Row,
  Col,
  Flex,
  Dropdown,
  MenuProps,
} from "antd";

import { DownOutlined } from "@ant-design/icons";

import ObjectForm from "components/Admin/ObjectForm";

import { RootState } from "Redux/store";
import { fetchUserArea, createUserArea, updateUserArea, deleteUserArea } from 'Redux/Admin/GoalsManager/UserAreas/action';

import { settings, initialSettings, initialValues } from './const';
import { ADMIN_PATH } from "utils/routes";
import { IUserArea } from 'Redux/Admin/GoalsManager/UserAreas/type';

const UserArea = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const { entities, loading, error } = useSelector((state: RootState) => state.fetchUserAreas);
  const [modal, contextHolder] = Modal.useModal();

  const ReachableContext = createContext<string | null>(null);

  useEffect(() => {
    if (id && !isNew) {
      dispatch(fetchUserArea(id));
    }
  }, [dispatch, isNew, id]);

  const onFinish = (values: any) => {
    if (isNew) {
      dispatch(createUserArea(values, handleCreate));
    }
    else if (id) {
      dispatch(updateUserArea(values, id));
    }
  };

  const handleCreate = (userArea: IUserArea) => {
    if (userArea && userArea.id) {
      navigate(`${ADMIN_PATH}/goals/journeys/user-areas/${userArea.id}`);
    }
  }

  if (!id) {
    navigate(`${ADMIN_PATH}/goals/journeys/user-areas`, { replace: true });
    return null;
  }

  const userArea = isNew ? initialValues : entities[id];

  if (!userArea) {
    return <Spin />;
  }

  const menuItems: MenuProps['items'] = [
    {
      label: 'View Goals',
      key: '1',
    },
    {
      label: 'Delete User Area',
      key: '2',
      danger: true,
    }
  ];

  const modalConfig = {
    title: 'Delete the User Area?',
    content: <span>Are you sure you want to delete the User Area <b>{userArea.id}</b>?</span>,
    confirmLoading: true
  };

  const handleMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === '1') {
      navigate(`${ADMIN_PATH}/goals/journeys/user-goals/?area=${id}`);
    }
    else if (e.key === '2') {
      const confirmed = await modal.confirm(modalConfig);

      if (confirmed) {
        dispatch(deleteUserArea(id, () => {
          navigate(`${ADMIN_PATH}/goals/journeys/user-areas`, { replace: true });
        }));
      }
    }
  };

  const menuProps = {
    items: menuItems,
    onClick: handleMenuClick,
  };

  return (
    <ReachableContext.Provider value="Light">
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Row>
          <Col span={12}>
            <Flex
              gap="middle"
              align="center"
              justify="space-between"
              style={{ marginBottom: 16 }}
            >
              <Link to={`${ADMIN_PATH}/goals/journeys/user-areas`}>Back</Link>
            </Flex>

            <Card title={`${isNew ? 'Create' : '' } User Area`} extra={
              isNew ? null : <Dropdown menu={menuProps} placement="bottom">
                <Button>
                  <Space>
                    Manage Area
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            }>
              <ObjectForm
                data={userArea}
                errorData={error}
                isNew={isNew}
                settings={isNew ? initialSettings : settings}
                loading={loading}
                onFinish={onFinish}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {contextHolder}
    </ReachableContext.Provider>
  );
}

export default UserArea;
